import { useMemo, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import {
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
    HomeIcon,
} from '@heroicons/react/24/outline';
import {
    CATEGORY_INFO,
    getCategorySidebarItems,
    findCategoryForPath,
    isCategoryLinkActive,
} from '../data/adminCardData';

interface CategoryLayoutProps {
    categoryKey?: string;
}

export default function CategoryLayout({ categoryKey: propCategoryKey }: CategoryLayoutProps) {
    const { isDark: isDarkMode } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(false);

    const categoryKey = propCategoryKey || findCategoryForPath(location.pathname);

    const categoryInfo = (CATEGORY_INFO as Record<string, { name: string; gradient: { dark: string; light: string } }>)[categoryKey as string] || { name: 'Other', gradient: { dark: 'from-slate-400 to-gray-400', light: 'from-gray-600 to-slate-600' } };
    const sidebarItems = useMemo(() => getCategorySidebarItems(categoryKey as string), [categoryKey]);

    if (!categoryKey) {
        return <Outlet />;
    }

    const gradientClass = isDarkMode ? categoryInfo.gradient.dark : categoryInfo.gradient.light;

    const sidebarWidth = collapsed ? 'w-16 min-w-[4rem]' : 'w-[260px] min-w-[260px]';

    const sidebarContent = (
        <>
            <div className={`flex items-center gap-2 border-b p-4 ${collapsed ? 'justify-center' : 'justify-between'} ${isDarkMode ? 'border-slate-700/50' : 'border-gray-200'}`}>
                {!collapsed && (
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <button
                            type="button"
                            onClick={() => navigate('/admin')}
                            title="Back to Dashboard"
                            className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-colors ${
                                isDarkMode ? 'bg-white/10 text-slate-300 hover:bg-white/15' : 'bg-black/5 text-gray-500 hover:bg-black/10'
                            }`}
                        >
                            <HomeIcon className="h-4 w-4" />
                        </button>
                        <h2 className={`truncate text-base font-bold bg-gradient-to-br ${gradientClass} bg-clip-text text-transparent`}>
                            {categoryInfo.name}
                        </h2>
                    </div>
                )}
                <button
                    type="button"
                    onClick={() => setCollapsed(!collapsed)}
                    title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    className={`hidden md:flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md transition-colors ${
                        isDarkMode ? 'text-slate-300 hover:bg-white/10' : 'text-gray-500 hover:bg-black/5'
                    }`}
                >
                    {collapsed
                        ? <ChevronDoubleRightIcon className="h-3.5 w-3.5" />
                        : <ChevronDoubleLeftIcon className="h-3.5 w-3.5" />
                    }
                </button>
            </div>

            <nav className={`flex flex-1 flex-col gap-0.5 overflow-y-auto ${collapsed ? 'p-1.5' : 'p-2'}`}>
                {sidebarItems.map((item) => {
                    const active = isCategoryLinkActive(item.link, location.pathname, location.search);
                    const IconComponent = item.icon as React.ComponentType<{ className?: string }>;

                    return (
                        <NavLink
                            key={item.name}
                            to={item.link}
                            title={collapsed ? `${item.name} — ${item.description}` : item.description}
                            className={`flex items-center gap-2.5 rounded-lg transition-colors min-h-[44px] ${
                                collapsed ? 'justify-center p-2.5' : 'px-2.5 py-2'
                            } ${
                                active
                                    ? isDarkMode
                                        ? 'bg-indigo-500/25 border-l-[3px] border-indigo-400'
                                        : 'bg-indigo-500/10 border-l-[3px] border-indigo-500'
                                    : 'border-l-[3px] border-transparent hover:bg-black/5 dark:hover:bg-white/5'
                            }`}
                        >
                            <div
                                className={`${item.color} flex flex-shrink-0 items-center justify-center rounded-md text-white ${
                                    collapsed ? 'h-8 w-8' : 'h-7 w-7'
                                } ${active ? 'opacity-100' : 'opacity-85'}`}
                            >
                                <IconComponent className="h-3.5 w-3.5" />
                            </div>
                            {!collapsed && (
                                <div className="min-w-0 flex-1">
                                    <div className={`truncate text-[13px] leading-tight ${active ? 'font-semibold' : 'font-medium'} ${isDarkMode ? 'text-slate-100' : 'text-gray-800'}`}>
                                        {item.name}
                                    </div>
                                    <div className={`truncate text-xs leading-tight mt-px ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>
                                        {item.description}
                                    </div>
                                </div>
                            )}
                        </NavLink>
                    );
                })}
            </nav>

            {!collapsed && (
                <div className={`border-t px-4 py-3 text-xs ${isDarkMode ? 'border-white/10 text-slate-400' : 'border-gray-200 text-gray-400'}`}>
                    {sidebarItems.length} items
                </div>
            )}
        </>
    );

    return (
        <div className="relative flex h-[calc(100dvh-64px)] overflow-hidden">
            <aside
                className={`relative z-50 hidden h-full flex-col overflow-hidden border-r md:flex ${sidebarWidth} ${isDarkMode ? 'border-slate-700/50 bg-[#253347] shadow-[2px_0_12px_rgba(0,0,0,0.4)]' : 'border-gray-200 bg-white shadow-sm'}`}
            >
                {sidebarContent}
            </aside>

            <div className={`flex min-w-0 flex-1 flex-col overflow-hidden ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
                <main className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
